import { format } from 'date-fns';

/**
 * Formats a phone number into different styles
 * @param {string} phone - The raw phone number
 * @returns {Object} Different phone number formats
 */
function formatPhoneNumber(phone) {
  // Remove all non-digits
  const cleaned = ('' + phone).replace(/\D/g, '');
  
  // Remove leading 1 if present
  const number = cleaned.startsWith('1') ? cleaned.slice(1) : cleaned;
  
  // Format the phone number
  const areaCode = number.slice(0, 3);
  const prefix = number.slice(3, 6);
  const line = number.slice(6, 10);

  return {
    unformattedLong: cleaned,
    unformattedShort: number,
    formattedLong: `+1 (${areaCode}) ${prefix}-${line}`,
    formattedShort: `(${areaCode}) ${prefix}-${line}`
  };
};

/**
 * Formats a date in different styles
 * @param {Date} date - The date to format
 * @returns {Object} Different date formats
 */
function formatDate(date) {
  return {
    full: format(date, 'EEEE, MMMM do, yyyy'),
    long: format(date, 'MMMM do, yyyy'),
    medium: format(date, 'MMM d, yyyy'),
    short: format(date, 'M/d/yy')
  };
};

/**
 * Substitutes template variables in a message with actual values
 * @param {string} messageText - The message template containing variables like {{name}}
 * @param {Object} member - The member object containing name, phone, email, etc.
 * @param {Object} options - Optional settings like clubName
 * @returns {Object} The message with all variables substituted and substitution values
 */
function substituteVariables(messageText, member, options = {}) {
  if (!member) {
    throw new Error('Member data is required for variable substitution');
  }

  const now = new Date();
  const phoneFormats = formatPhoneNumber(member.phone_number || '');
  const dateFormats = formatDate(now);

  // Debug member data
  console.log('Member data:', {
    first_name: member.first_name,
    last_name: member.last_name,
    full_name: member.full_name,
    email: member.email,
    phone_number: member.phone_number
  });

  // Basic variables without formatting options
  const basicVariables = {
    firstName: member.first_name || '',
    lastName: member.last_name || '',
    fullName: member.full_name || '',
    name: member.full_name || '', // Add backward compatibility for {{name}}
    email: member.email || '',
    clubName: options.clubName || 'Rotary Club'
  };

  // Debug variables
  console.log('Basic variables:', basicVariables);

  // Variables with formatting options
  const formattedVariables = {
    'phone:unformattedLong': phoneFormats.unformattedLong,
    'phone:unformattedShort': phoneFormats.unformattedShort,
    'phone:formattedLong': phoneFormats.formattedLong,
    'phone:formattedShort': phoneFormats.formattedShort,
    'sendDate:full': dateFormats.full,
    'sendDate:long': dateFormats.long,
    'sendDate:medium': dateFormats.medium,
    'sendDate:short': dateFormats.short
  };

  // Debug formatted variables
  console.log('Formatted variables:', formattedVariables);

  // Create substitution values object for history
  const substitutionValues = {
    ...basicVariables,
    phone: phoneFormats,
    sendDate: dateFormats
  };

  // Combine all variables for substitution
  const variables = {
    ...basicVariables,
    ...formattedVariables
  };

  // Do the substitution with debugging
  const substitutedMessage = messageText.replace(/\{\{([^}]+)\}\}/g, (match, variable) => {
    const trimmedVar = variable.trim();
    const value = variables[trimmedVar];
    console.log('Substituting:', {
      match,
      variable: trimmedVar,
      value,
      availableVariables: Object.keys(variables)
    });
    if (!value && value !== '') {
      console.warn(`Variable ${trimmedVar} not found in message template`);
      return match;  // Keep the original {{variable}} if not found
    }
    return value;
  });

  // Debug final result
  console.log('Substitution result:', {
    original: messageText,
    substituted: substitutedMessage,
    values: substitutionValues
  });

  return {
    substituted: substitutedMessage,
    substitutionValues
  };
}

/**
 * Gets a list of all available template variables with descriptions
 * @returns {Array<{variable: string, description: string}>}
 */
function getAvailableVariables() {
  return [
    { variable: '{{firstName}}', description: 'First name of the member' },
    { variable: '{{lastName}}', description: 'Last name of the member' },
    { variable: '{{fullName}}', description: 'Full name of the member' },
    { variable: '{{name}}', description: 'Full name of the member (backward compatibility)' },
    { variable: '{{email}}', description: 'Email address of the member' },
    { variable: '{{clubName}}', description: 'Name of the Rotary club' },
    { variable: '{{phone:unformattedLong}}', description: 'Phone number with country code (e.g., 16125551234)' },
    { variable: '{{phone:unformattedShort}}', description: 'Phone number without country code (e.g., 6125551234)' },
    { variable: '{{phone:formattedLong}}', description: 'Phone number with country code (+1 (612) 555-1234)' },
    { variable: '{{phone:formattedShort}}', description: 'Phone number without country code ((612) 555-1234)' },
    { variable: '{{sendDate:full}}', description: 'Full date (e.g., Saturday, December 28th, 2024)' },
    { variable: '{{sendDate:long}}', description: 'Long date (e.g., December 28th, 2024)' },
    { variable: '{{sendDate:medium}}', description: 'Medium date (e.g., Dec 28, 2024)' },
    { variable: '{{sendDate:short}}', description: 'Short date (e.g., 12/28/24)' }
  ];
}

export { substituteVariables, getAvailableVariables };
